import { ApiError, bootToken } from "./api.ts";

/**
 * Поиск по системе — клиент запроса и типы ответа.
 *
 * Свой файл, а не строка в `lib/api.ts`, по двум причинам. Первая формальная:
 * тот файл общий и правится другими руками. Вторая по существу: у поиска своя
 * дисциплина запроса, которой у остальных вызовов нет и не должно быть, — он
 * дёргается на каждую нажатую букву, а значит обязан уметь отменять
 * предыдущий ответ и не давать медленному ответу на «mo» перерисовать выдачу
 * по «mohammed». Ровно это здесь и живёт, рядом с типами, к которым относится.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Показателей и экранов: их поиск целиком клиентский и идёт по
 * тем же объектам, из которых рисуется справочник
 * (`content/metricsReference.ts`) и меню (`i18n/dict.ts`). Индекс, собранный
 * руками рядом с ними, разошёлся бы с ними при первой правке подписи — и это
 * не гипотеза, а то, чем уже болен поиск по справочнику: он ищет ТОЛЬКО по
 * текущему языку, и при английском интерфейсе «Реклама / выручка» не находится
 * ничем.
 */

export type SearchEntityKind =
  | "park"
  | "brand"
  | "channel"
  | "stage"
  | "manager"
  | "campaign";

export type SearchEntityHit = {
  kind: SearchEntityKind;
  key: string;
  label: string;
  hint: string | null;
  inactive: boolean;
};

export type SearchRecordHit = {
  lead_id: number;
  title: string | null;
  phone_e164: string | null;
  email: string | null;
  created: string;
  park_name: string | null;
  stage_name: string | null;
  manager_name: string | null;
  source_name: string | null;
  deal_id: number | null;
  deal_stage_name: string | null;
  deal_amount_aed_minor: string | null;
  bitrix_url: string | null;
  deal_bitrix_url: string | null;
};

export type SearchSettingHit = {
  kind: "setting" | "source";
  key: string;
  label: string;
  hint: string | null;
};

export type SearchResult = {
  q: string;
  minQuery: number;
  minRecordQuery: number;
  entities: SearchEntityHit[];
  entitiesTruncated: boolean;
  entityMore: Partial<Record<SearchEntityKind, number>>;
  /**
   * `null` — раздела НЕТ, потому что нет права `journey_lookup`. Это не то же
   * самое, что пустой раздел: пусто значит «искали и не нашли», отсутствие —
   * «не смотрели, и не покажем».
   */
  records: { rows: SearchRecordHit[]; total: number; truncated: boolean } | null;
  /** `null` — не администратор. */
  settings: SearchSettingHit[] | null;
  queryMs?: number;
};

/**
 * Один запрос на весь поиск.
 *
 * `signal` обязателен по смыслу, а не по удобству: без отмены выдача мигает
 * ответами на уже неактуальные запросы, и порядок их прихода не гарантирован —
 * ответ на «mo» может прийти после ответа на «mohammed».
 */
export async function searchAll(q: string, signal: AbortSignal): Promise<SearchResult> {
  const token = bootToken();
  const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, {
    credentials: "include",
    signal,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
      if (typeof body.error === "string" && body.error !== "") msg = body.error;
    } catch {
      /* тело не JSON — остаётся код */
    }
    throw new ApiError(res.status, msg, body);
  }
  return (await res.json()) as SearchResult;
}

/** Пустой ответ: им же рисуется состояние «ещё не искали». */
export const EMPTY_SEARCH: SearchResult = {
  q: "",
  minQuery: 2,
  minRecordQuery: 3,
  entities: [],
  entitiesTruncated: false,
  entityMore: {},
  records: null,
  settings: null,
};
